import {
  // eslint-disable-next-line import/named
  PayloadAction,
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { RootState } from '../../../stateStore'
import invoiceAuditReportApi from '../../../middleware/api/Invoicing/InvoiceAuditReport/invoiceAuditReportApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  GetCountryLookUpType,
  GetInvoiceListTypeResponse,
  InvoiceAuditListQueryParams,
  InvoiceAuditReportState,
} from '../../../types/Invoicing/InvoiceAuditReport/invoiceAuditReportType'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RatingCheckEnum } from '../../../types/Performance/ReviewList/reviewListTypes'
import {
  filterOptionsStatus,
  outgoingInitialGetInvoiceListProperties,
} from '../../../pages/Invoicing/InvoiceAuditReport/StatusFilterHelper'

const getInvoiceAuditList = createAsyncThunk(
  'invoicing/getInvoiceAuditList',
  async (queryParams: InvoiceAuditListQueryParams, thunkApi) => {
    try {
      return await invoiceAuditReportApi.getInvoiceAuditList(queryParams)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCountryLookup = createAsyncThunk(
  'invoicing/getCountryLookup',
  async (_, thunkApi) => {
    try {
      return await invoiceAuditReportApi.getCountryLookup()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const selectDate = 'Current Month'
const statusValue = 'SENT'

export const initialInvoiceAuditReportState: InvoiceAuditReportState = {
  getInvoiceList: {} as GetInvoiceListTypeResponse,
  getCountryLookUpList: [],
  isLoading: ApiLoadingState.idle,
  getInvoiceListResponese: {} as GetInvoiceListTypeResponse,
  listSize: 0,
  SelectValue: selectDate,
  StatusValue: statusValue,
  SelectCountry: '',
  customFromValue: '',
  customToValue: '',
  multiText: '',
  intrnalOrNot: false,
  OutgoingGetInvoiceListProperties: outgoingInitialGetInvoiceListProperties,
  checkedItems: ['SENT'],
}
const invoiceAuditReportSlice = createSlice({
  name: 'invoice Audit Report',
  initialState: initialInvoiceAuditReportState,
  reducers: {
    setSelectValue: (state, action) => {
      state.SelectValue = action.payload
    },
    setStatusValue: (state, action) => {
      state.StatusValue = action.payload
    },
    setSelectCountry: (state, action) => {
      state.SelectCountry = action.payload
    },
    setCustomFromValue: (state, action) => {
      state.customFromValue = action.payload
    },
    setCustomToValue: (state, action) => {
      state.customToValue = action.payload
    },
    setSearchInput: (state, action) => {
      state.multiText = action.payload
    },
    setIsInternalValue: (state, action) => {
      state.intrnalOrNot = action.payload
    },
    setCheckedItems: (state, action) => {
      state.checkedItems = action.payload
    },
    setStatusForGetInvoiceList: (
      state,
      action: PayloadAction<{
        type: RatingCheckEnum
        value: string
        indexVal: number
      }>,
    ) => {
      const { type, value, indexVal } = action.payload
      if (type === RatingCheckEnum.checkAll.toString()) {
        state.OutgoingGetInvoiceListProperties = {
          ...state.OutgoingGetInvoiceListProperties,
          status: filterOptionsStatus,
        }
      } else if (type === RatingCheckEnum.uncheckAll.toString()) {
        state.OutgoingGetInvoiceListProperties = {
          ...state.OutgoingGetInvoiceListProperties,
          status: [],
        }
      } else if (type === RatingCheckEnum.checkNum.toString()) {
        state.OutgoingGetInvoiceListProperties.status.push(value)
      } else {
        state.OutgoingGetInvoiceListProperties.status.splice(indexVal, 1)
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getInvoiceAuditList.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.getInvoiceList = action.payload
    })
    builder.addCase(getCountryLookup.fulfilled, (state, action) => {
      state.getCountryLookUpList = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addMatcher(
      isAnyOf(getInvoiceAuditList.pending, getCountryLookup.pending),
      (state) => {
        state.isLoading = ApiLoadingState.loading
      },
    )
  },
})
const invoiceAuditReportThunk = {
  getInvoiceAuditList,
  getCountryLookup,
}
const isLoading = (state: RootState): LoadingState =>
  state.invoiceAuditReportList.isLoading
const invoiceList = (state: RootState): GetInvoiceListTypeResponse =>
  state.invoiceAuditReportList.getInvoiceList
const countries = (state: RootState): GetCountryLookUpType[] =>
  state.invoiceAuditReportList.getCountryLookUpList
const listSize = (state: RootState): number =>
  state.invoiceAuditReportList.listSize
const getSelectValue = (state: RootState): string =>
  state.invoiceAuditReportList.SelectValue

const getStatusValue = (state: RootState): string =>
  state.invoiceAuditReportList.StatusValue

const getCountryValue = (state: RootState): string =>
  state.invoiceAuditReportList.SelectCountry
const getCustomFromValue = (state: RootState): string | Date | null =>
  state.invoiceAuditReportList.customFromValue

const getCustomToValue = (state: RootState): string | Date | null =>
  state.invoiceAuditReportList.customToValue
const getSearchInputValue = (state: RootState): string | null =>
  state.invoiceAuditReportList.multiText
const getIsInternalValue = (state: RootState): boolean =>
  state.invoiceAuditReportList.intrnalOrNot
const getCheckedItems = (state: RootState): string[] =>
  state.invoiceAuditReportList.checkedItems
const invoiceAuditReportSelectors = {
  isLoading,
  invoiceList,
  countries,
  listSize,
  getSelectValue,
  getStatusValue,
  getCountryValue,
  getCustomFromValue,
  getCustomToValue,
  getSearchInputValue,
  getIsInternalValue,
  getCheckedItems,
}
export const invoiceAuditReportService = {
  ...invoiceAuditReportThunk,
  actions: invoiceAuditReportSlice.actions,
  selectors: invoiceAuditReportSelectors,
}
export default invoiceAuditReportSlice.reducer
